import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import Remove from "@mui/icons-material/Remove";
import { Box, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";

import StepperButton from "./stepper-button";
import type { WindowValue } from "./use-window-stepper";
import { STEP_MINUTES, useWindowStepper } from "./use-window-stepper";

interface CustomWindowCardProps {
  selected: boolean;
  shiftStart: Date;
  shiftEnd: Date;
  minMinutes: number;
  maxMinutes?: number;
  value: WindowValue;
  // Fired when a collapsed (unselected) card is clicked.
  onSelect: () => void;
  // Fired on every accepted step with the resulting window.
  onChange: (start: Date, end: Date) => void;
  headerLabel: string;
  hintLabel: string;
  startLabel: string;
  endLabel: string;
}

// Selectable "Custom Window" card with 30-minute steppers. Presentational shell
// around the shared useWindowStepper hook so the worker app and the supervisor
// Force Assign popover render an identical control.
const CustomWindowCard: React.FC<CustomWindowCardProps> = ({
  selected,
  shiftStart,
  shiftEnd,
  minMinutes,
  maxMinutes,
  value,
  onSelect,
  onChange,
  headerLabel,
  hintLabel,
  startLabel,
  endLabel,
}) => {
  const {
    start,
    end,
    canStartMinus,
    canStartPlus,
    canEndMinus,
    canEndPlus,
    caption,
    stepStart,
    stepEnd,
  } = useWindowStepper({
    shiftStart,
    shiftEnd,
    minMinutes,
    maxMinutes,
    value,
    onChange,
    enabled: selected,
  });

  const timeValueSx = {
    fontSize: "15px",
    fontWeight: 600,
    color: "#2F4D8B",
    minWidth: "58px",
    textAlign: "center",
    whiteSpace: "nowrap",
  } as const;

  const fieldLabelSx = {
    fontSize: "11px",
    fontWeight: 500,
    color: "#808080",
    letterSpacing: "0.3px",
  } as const;

  const stepperRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
  } as const;

  return (
    <Box
      onClick={() => {
        if (!selected) {
          onSelect();
        }
      }}
      sx={{
        border: selected ? "2px solid #2F4D8B" : "2px solid transparent",
        backgroundColor: selected ? "#FFFFFF" : "rgba(24, 37, 62, 0.08)",
        borderRadius: "16px",
        padding: "16px 10px 18px",
        marginTop: "8px",
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: selected ? "#2F4D8B" : "rgba(0, 0, 0, 0.87)",
          }}
        >
          {headerLabel}
        </Typography>
        {selected && <Check sx={{ fontSize: 16, color: "#2F4D8B" }} />}
      </Box>
      <Typography
        sx={{
          fontSize: "11px",
          color: "#808080",
          textAlign: "center",
          marginTop: "2px",
        }}
      >
        {hintLabel}
      </Typography>

      {selected && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px",
              marginTop: "14px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography sx={fieldLabelSx}>{startLabel}</Typography>
              <Box sx={stepperRowSx}>
                <StepperButton
                  ariaLabel="Decrease start time by 30 minutes"
                  disabled={!canStartMinus}
                  onStep={() => stepStart(-STEP_MINUTES)}
                >
                  <Remove sx={{ fontSize: 20 }} />
                </StepperButton>
                <Typography aria-live="polite" sx={timeValueSx}>
                  {start ? format(start, "h:mm a") : "--"}
                </Typography>
                <StepperButton
                  ariaLabel="Increase start time by 30 minutes"
                  disabled={!canStartPlus}
                  onStep={() => stepStart(STEP_MINUTES)}
                >
                  <Add sx={{ fontSize: 20 }} />
                </StepperButton>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography sx={fieldLabelSx}>{endLabel}</Typography>
              <Box sx={stepperRowSx}>
                <StepperButton
                  ariaLabel="Decrease end time by 30 minutes"
                  disabled={!canEndMinus}
                  onStep={() => stepEnd(-STEP_MINUTES)}
                >
                  <Remove sx={{ fontSize: 20 }} />
                </StepperButton>
                <Typography aria-live="polite" sx={timeValueSx}>
                  {end ? format(end, "h:mm a") : "--"}
                </Typography>
                <StepperButton
                  ariaLabel="Increase end time by 30 minutes"
                  disabled={!canEndPlus}
                  onStep={() => stepEnd(STEP_MINUTES)}
                >
                  <Add sx={{ fontSize: 20 }} />
                </StepperButton>
              </Box>
            </Box>
          </Box>

          {caption && (
            <Typography
              sx={{
                fontSize: "11px",
                color: "#808080",
                textAlign: "center",
                marginTop: "12px",
              }}
            >
              {caption}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default CustomWindowCard;
